import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { ThemeProvider } from 'styled-components';

import { ShortCodeService } from '@tager/web-core';

import { useTypedSelector } from '@/store/store';
import { selectSinglePost } from '@/store/reducers/single-post';
import { colors } from '@/constants/theme';
import { media } from '@/utils/mixin';
import { ScrollStyles } from '@/components/styles';
import { useStyledComponentsTheme } from '@/theme/hooks';

import GallerySlider from './components/GallerySlider';
import ListOfContents from './components/ListOfContents';

const galleryShortCodeService = new ShortCodeService('gallery', ['number']);
const contentsShortCodeService = new ShortCodeService('contents');
const contentsAnchorShortCodeService = new ShortCodeService('contents-item', [
  'index',
]);

function Content() {
  const theme = useStyledComponentsTheme();
  const page = useTypedSelector(selectSinglePost);
  const [content, setContent] = useState<string>(page?.body ?? '');

  useEffect(() => {
    if (!page || !page.body) {
      return;
    }

    let replacedText = page.body;

    replacedText =
      galleryShortCodeService.replaceShortCodeByElement(replacedText);

    replacedText =
      contentsShortCodeService.replaceShortCodeByElement(replacedText);

    replacedText =
      contentsAnchorShortCodeService.replaceShortCodeByElement(replacedText);

    setContent(replacedText);
  }, [page, page?.body]);

  useEffect(() => {
    galleryShortCodeService.renderComponent(({ number }) => {
      const gallery = page?.galleries?.[Number(number) - 1];

      if (!gallery) {
        return null;
      }

      return (
        <ThemeProvider theme={theme}>
          <GallerySlider gallery={gallery} />
        </ThemeProvider>
      );
    });
  }, [content, page?.galleries, theme]);

  useEffect(() => {
    contentsShortCodeService.renderComponent(() => {
      return (
        <ThemeProvider theme={theme}>
          <ListOfContents contents={page?.contents ?? []} />
        </ThemeProvider>
      );
    });
  }, [content, page?.contents, theme]);

  useEffect(() => {
    contentsAnchorShortCodeService.renderComponent(({ index }) => {
      return (
        <ThemeProvider theme={theme}>
          <div id={`contents-item-${index}`} />
        </ThemeProvider>
      );
    });
  }, [content, page?.contents, theme]);

  if (!page) {
    return null;
  }

  return (
    <Component
      id="post-content"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}

export default Content;

const Component = styled.section`
  margin-top: 30px;
  color: ${(props) => props.theme.black200};

  p {
    font-size: 16px;
    line-height: 24px;
    margin: 0 0 1.5vw;
    color: ${(props) => props.theme.black200};

    ${media.tablet(css`
      font-size: 14px;
      line-height: 20px;
      margin: 0 0 2vw;
    `)}
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 10px 0;
    color: ${(props) => props.theme.black};
  }

  h3 {
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
  }

  div[data-short-code='gallery'] {
    margin: 0 0 1.5vw;

    ${media.tablet(css`
      margin: 0 0 2vw;
    `)}
  }

  ul,
  ol {
    margin: 20px 0 20px 2vw;

    li {
      margin-bottom: 5px;
      vertical-align: top;
      padding-left: 0.6vw;
      font-size: 16px;
      line-height: 24px;
      color: ${(props) => props.theme.black200};

      ${media.tablet(css`
        font-size: 14px;
        line-height: 20px;
        margin: 0;
      `)}
    }
  }

  ul {
    list-style-type: square;
  }

  ol {
    list-style-type: decimal;
  }

  figure {
    margin: 1vw 0;
    overflow: auto;

    ${ScrollStyles}

    &::-webkit-scrollbar-thumb {
      height: 50px;
    }

    table {
      width: 100%;
      min-width: 585px;
      border-collapse: collapse;
      color: ${(props) => props.theme.black};
      border: 1px solid ${colors.gray400};

      tbody {
        tr {
          th,
          td {
            min-width: 190px;
            padding: 5px;
            border: 1px solid ${colors.gray400};

            &:not(:last-child) {
              border-bottom-width: 0;
            }

            p {
              text-align: left !important;
              margin: 0;
            }
          }
        }
      }
    }
  }

  img {
    display: block;
    max-width: 100%;
    margin: 1vw 0;
  }

  blockquote {
    padding: 20px 30px 20px 27px;
    border-left: 3px solid ${colors.main};
    background: ${(props) => props.theme.gray_1};
    color: ${(props) => props.theme.black200};
    font-size: 16px;
    line-height: 24px;
    margin: 25px 0;
  }

  a {
    color: ${colors.main};
  }
`;
